import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Shield,
  Settings,
  Users,
  Server,
  Palette,
  Mail,
  Save,
  LogOut,
  Sun,
  Moon,
  Image,
  Trash2,
  UserCog,
  Check,
  X,
  RefreshCw,
  Globe,
  Plus,
  Pencil,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const colorOptions = [
  { value: '#0066DA', label: 'Bleu Pro' },
  { value: '#22C55E', label: 'Vert' },
  { value: '#8B5CF6', label: 'Violet' },
  { value: '#EF4444', label: 'Rouge' },
  { value: '#F97316', label: 'Orange' },
  { value: '#0EA5E9', label: 'Bleu Ciel' },
  { value: '#EC4899', label: 'Rose' },
  { value: '#14B8A6', label: 'Teal' },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    app_name: 'ProMail',
    logo_url: '',
    primary_color: '#0066DA',
    imap_host: '',
    imap_port: 993,
    imap_secure: true,
    smtp_host: '',
    smtp_port: 465,
    smtp_secure: true,
  });
  const [users, setUsers] = useState([]);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [domains, setDomains] = useState([]);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [domainForm, setDomainForm] = useState({
    domain: '', display_name: '', imap_host: '', imap_port: 993, imap_secure: true,
    smtp_host: '', smtp_port: 465, smtp_secure: true, enabled: true,
  });
  const logoInputRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('promail_admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    loadData();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, usersRes, domainsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/config`),
        axios.get(`${API_URL}/admin/users`),
        axios.get(`${API_URL}/admin/domains`),
      ]);
      setConfig(configRes.data);
      setUsers(usersRes.data.users);
      setDomains(domainsRes.data.domains || []);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expirée ou accès refusé');
        handleLogout();
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/config`, config);
      toast.success('Configuration enregistrée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setConfig(prev => ({ ...prev, logo_url: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserId) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${deleteUserId}`);
      setUsers(users.filter(u => u.id !== deleteUserId));
      toast.success('Utilisateur supprimé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleteUserId(null);
    }
  };

  const handleToggleAdmin = async (userId) => {
    try {
      const response = await axios.put(`${API_URL}/admin/users/${userId}/toggle-admin`);
      setUsers(users.map(u => 
        u.id === userId ? { ...u, is_admin: response.data.is_admin } : u
      ));
      toast.success(`Statut admin modifié`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  // Domain management
  const openNewDomain = () => {
    setEditingDomain(null);
    setDomainForm({
      domain: '', display_name: '', imap_host: '', imap_port: 993, imap_secure: true,
      smtp_host: '', smtp_port: 465, smtp_secure: true, enabled: true,
    });
    setDomainDialogOpen(true);
  };

  const openEditDomain = (d) => {
    setEditingDomain(d);
    setDomainForm({
      domain: d.domain, display_name: d.display_name, imap_host: d.imap_host,
      imap_port: d.imap_port, imap_secure: d.imap_secure, smtp_host: d.smtp_host,
      smtp_port: d.smtp_port, smtp_secure: d.smtp_secure, enabled: d.enabled,
    });
    setDomainDialogOpen(true);
  };

  const handleSaveDomain = async () => {
    if (!domainForm.domain || !domainForm.imap_host || !domainForm.smtp_host) {
      toast.error('Domaine, hôte IMAP et hôte SMTP sont requis');
      return;
    }
    try {
      if (editingDomain) {
        await axios.put(`${API_URL}/admin/domains/${editingDomain.id}`, domainForm);
        toast.success('Domaine mis à jour');
      } else {
        await axios.post(`${API_URL}/admin/domains`, domainForm);
        toast.success('Domaine ajouté');
      }
      setDomainDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteDomain = async (domainId) => {
    try {
      await axios.delete(`${API_URL}/admin/domains/${domainId}`);
      setDomains(domains.filter(d => d.id !== domainId));
      toast.success('Domaine supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('promail_admin_token');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/admin');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse-subtle">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="h-16 border-b border-border bg-card flex items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold font-['Plus_Jakarta_Sans']">Administration ProMail</h1>
            <p className="text-xs text-muted-foreground">Panneau de configuration</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={loadData}>
            <RefreshCw className="w-5 h-5" />
          </Button>
          <Separator orientation="vertical" className="h-8 mx-2" />
          <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-6">
        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="general" className="gap-2">
              <Palette className="w-4 h-4" />
              Général
            </TabsTrigger>
            <TabsTrigger value="domains" className="gap-2" data-testid="tab-domains">
              <Globe className="w-4 h-4" />
              Domaines
            </TabsTrigger>
            <TabsTrigger value="server" className="gap-2">
              <Server className="w-4 h-4" />
              Serveur
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="w-4 h-4" />
              Utilisateurs
            </TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Plus_Jakarta_Sans']">Personnalisation de l'application</CardTitle>
                <CardDescription>
                  Configurez le nom, logo et couleurs de l'application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Nom de l'application</Label>
                  <Input
                    value={config.app_name}
                    onChange={(e) => setConfig({ ...config, app_name: e.target.value })}
                    placeholder="ProMail"
                    data-testid="admin-app-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Logo</Label>
                  <div className="flex items-center gap-4">
                    {config.logo_url ? (
                      <div className="w-20 h-20 rounded-lg border border-border overflow-hidden bg-white">
                        <img src={config.logo_url} alt="Logo" className="w-full h-full object-contain" />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-lg flex items-center justify-center" style={{ backgroundColor: config.primary_color }}>
                        <Mail className="w-10 h-10 text-white" />
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                      >
                        <Image className="w-4 h-4 mr-2" />
                        {config.logo_url ? 'Changer le logo' : 'Ajouter un logo'}
                      </Button>
                      {config.logo_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfig({ ...config, logo_url: '' })}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </Button>
                      )}
                    </div>
                    <input
                      type="file"
                      ref={logoInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={handleLogoUpload}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Recommandé : image carrée, format PNG ou SVG
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Couleur principale</Label>
                  <div className="grid grid-cols-4 gap-4">
                    {colorOptions.map((color) => (
                      <button
                        key={color.value}
                        className={cn(
                          "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all",
                          config.primary_color === color.value
                            ? "border-primary ring-2 ring-primary/20"
                            : "border-border hover:border-muted-foreground"
                        )}
                        onClick={() => setConfig({ ...config, primary_color: color.value })}
                      >
                        <div
                          className="w-8 h-8 rounded-full"
                          style={{ backgroundColor: color.value }}
                        />
                        <span className="text-xs">{color.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <Button onClick={handleSaveConfig} disabled={saving} data-testid="save-general-config">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="font-['Plus_Jakarta_Sans']">Aperçu</CardTitle>
                <CardDescription>Visualisez l'apparence de l'application</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                  {config.logo_url ? (
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-white">
                      <img src={config.logo_url} alt="Logo" className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: config.primary_color }}>
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-bold font-['Plus_Jakarta_Sans']">{config.app_name || 'ProMail'}</h3>
                    <p className="text-sm text-muted-foreground">Messagerie professionnelle</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* Domains Management */}
          <TabsContent value="domains" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-['Plus_Jakarta_Sans']">Gestion des domaines</CardTitle>
                  <CardDescription>
                    Configurez les serveurs IMAP/SMTP pour chaque domaine email
                  </CardDescription>
                </div>
                <Button onClick={openNewDomain} data-testid="add-domain-btn">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter un domaine
                </Button>
              </CardHeader>
              <CardContent>
                {domains.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Globe className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p>Aucun domaine configuré</p>
                    <p className="text-sm mt-1">Ajoutez un domaine pour permettre la connexion multi-domaines</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {domains.map((d) => (
                      <div key={d.id} className={cn("flex items-center justify-between p-4 rounded-lg border", !d.enabled && "opacity-60")} data-testid={`domain-card-${d.id}`}>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Globe className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{d.display_name} <span className="text-muted-foreground font-normal">({d.domain})</span></p>
                            <p className="text-xs text-muted-foreground">
                              IMAP: {d.imap_host}:{d.imap_port} | SMTP: {d.smtp_host}:{d.smtp_port}
                            </p>
                          </div>
                          {!d.enabled && <Badge variant="secondary">Désactivé</Badge>}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditDomain(d)} data-testid={`edit-domain-${d.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteDomain(d.id)} data-testid={`delete-domain-${d.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Domain Dialog */}
            <Dialog open={domainDialogOpen} onOpenChange={setDomainDialogOpen}>
              <DialogContent className="max-w-lg" data-testid="domain-dialog">
                <DialogHeader>
                  <DialogTitle>{editingDomain ? 'Modifier le domaine' : 'Ajouter un domaine'}</DialogTitle>
                  <DialogDescription>
                    Configurez le serveur IMAP/SMTP pour ce domaine
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Domaine</Label>
                      <Input
                        value={domainForm.domain}
                        onChange={(e) => setDomainForm(p => ({ ...p, domain: e.target.value }))}
                        placeholder="@domaine.com"
                        data-testid="domain-input"
                      />
                    </div>
                    <div>
                      <Label>Nom d'affichage</Label>
                      <Input
                        value={domainForm.display_name}
                        onChange={(e) => setDomainForm(p => ({ ...p, display_name: e.target.value }))}
                        placeholder="Mon Entreprise"
                      />
                    </div>
                  </div>
                  <Separator />
                  <p className="text-sm font-medium">IMAP (Réception)</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <Label className="text-xs">Hôte IMAP</Label>
                      <Input value={domainForm.imap_host} onChange={(e) => setDomainForm(p => ({ ...p, imap_host: e.target.value }))} placeholder="mail.domaine.com" className="h-8 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs">Port</Label>
                      <Input type="number" value={domainForm.imap_port} onChange={(e) => setDomainForm(p => ({ ...p, imap_port: parseInt(e.target.value) || 993 }))} className="h-8 text-sm" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={domainForm.imap_secure} onCheckedChange={(v) => setDomainForm(p => ({ ...p, imap_secure: v }))} />
                    <Label className="text-sm">SSL/TLS</Label>
                  </div>
                  <Separator />
                  <p className="text-sm font-medium">SMTP (Envoi)</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <Label className="text-xs">Hôte SMTP</Label>
                      <Input value={domainForm.smtp_host} onChange={(e) => setDomainForm(p => ({ ...p, smtp_host: e.target.value }))} placeholder="smtp.domaine.com" className="h-8 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs">Port</Label>
                      <Input type="number" value={domainForm.smtp_port} onChange={(e) => setDomainForm(p => ({ ...p, smtp_port: parseInt(e.target.value) || 465 }))} className="h-8 text-sm" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={domainForm.smtp_secure} onCheckedChange={(v) => setDomainForm(p => ({ ...p, smtp_secure: v }))} />
                    <Label className="text-sm">SSL/TLS</Label>
                  </div>
                  <Separator />
                  <div className="flex items-center gap-2">
                    <Switch checked={domainForm.enabled} onCheckedChange={(v) => setDomainForm(p => ({ ...p, enabled: v }))} />
                    <Label>Domaine activé</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDomainDialogOpen(false)}>Annuler</Button>
                  <Button onClick={handleSaveDomain} data-testid="save-domain-btn">Enregistrer</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>


          {/* Server Settings */}
          <TabsContent value="server" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Plus_Jakarta_Sans']">Configuration IMAP</CardTitle>
                <CardDescription>
                  Paramètres du serveur de réception des emails
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hôte IMAP</Label>
                    <Input
                      value={config.imap_host}
                      onChange={(e) => setConfig({ ...config, imap_host: e.target.value })}
                      placeholder="imap.exemple.com"
                      data-testid="admin-imap-host"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Port IMAP</Label>
                    <Input
                      type="number"
                      value={config.imap_port}
                      onChange={(e) => setConfig({ ...config, imap_port: parseInt(e.target.value) || 993 })}
                      placeholder="993"
                      data-testid="admin-imap-port"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">Connexion sécurisée (SSL/TLS)</p>
                    <p className="text-sm text-muted-foreground">Recommandé pour la sécurité</p>
                  </div>
                  <Switch
                    checked={config.imap_secure}
                    onCheckedChange={(v) => setConfig({ ...config, imap_secure: v })}
                    data-testid="admin-imap-secure"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-['Plus_Jakarta_Sans']">Configuration SMTP</CardTitle>
                <CardDescription>
                  Paramètres du serveur d'envoi des emails
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hôte SMTP</Label>
                    <Input
                      value={config.smtp_host}
                      onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })}
                      placeholder="smtp.exemple.com"
                      data-testid="admin-smtp-host"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Port SMTP</Label>
                    <Input
                      type="number"
                      value={config.smtp_port}
                      onChange={(e) => setConfig({ ...config, smtp_port: parseInt(e.target.value) || 465 })}
                      placeholder="465"
                      data-testid="admin-smtp-port"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">Connexion sécurisée (SSL/TLS)</p>
                    <p className="text-sm text-muted-foreground">Recommandé pour la sécurité</p>
                  </div>
                  <Switch
                    checked={config.smtp_secure}
                    onCheckedChange={(v) => setConfig({ ...config, smtp_secure: v })}
                    data-testid="admin-smtp-secure"
                  />
                </div>

                <Button onClick={handleSaveConfig} disabled={saving} data-testid="save-server-config">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Enregistrement...' : 'Enregistrer la configuration serveur'}
                </Button>
              </CardContent>
            </Card>

            {/* Current Config Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="font-['Plus_Jakarta_Sans']">Résumé de la configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="font-medium mb-1">IMAP (Réception)</p>
                    <code className="text-primary">{config.imap_host || '(non configuré)'}:{config.imap_port}</code>
                    <span className="ml-2">{config.imap_secure ? '(SSL)' : ''}</span>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="font-medium mb-1">SMTP (Envoi)</p>
                    <code className="text-primary">{config.smtp_host || '(non configuré)'}:{config.smtp_port}</code>
                    <span className="ml-2">{config.smtp_secure ? '(SSL)' : ''}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Management */}
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Plus_Jakarta_Sans']">Gestion des utilisateurs</CardTitle>
                <CardDescription>
                  {users.length} utilisateur(s) enregistré(s)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{user.name}</p>
                            {user.is_admin && (
                              <Badge className="bg-primary">Admin</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleAdmin(user.id)}
                          data-testid={`toggle-admin-${user.id}`}
                        >
                          <UserCog className="w-4 h-4 mr-2" />
                          {user.is_admin ? 'Retirer admin' : 'Rendre admin'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:bg-destructive hover:text-white"
                          onClick={() => setDeleteUserId(user.id)}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete User Dialog */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Toutes les données de l'utilisateur (emails, contacts, tâches, etc.) seront supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-destructive text-white hover:bg-destructive/90">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
